import {
  getFormatNumPlus,
  getSuitMaxNumber,
  getFormatPercentPlus,
  toFirstUpperCase,
} from '@gp/utils/common';
import { ECHART_COLOR } from '@gp/constants/const';
import { BASE_ECHART_OPTION, BASE_BAR_SERIES_OPTION, Y_AXIS_NAME_CURRENCY_GRID_OPTION, Y_AXIS_NAME_CURRENCY_OPTION } from '@gp/constants/echarts';

const BAR_LINE_OPTS: any = {
  metric: ['Gross Revenue', 'Net Profit', 'Profit Margin'], // 指标名称
  res_metric: ['revenue', 'profit', 'margin'], // 接口返回对应字段名称
};

const getNum = (symbol: string): number => {
  let res = 1;
  switch (symbol) {
    case 'K':
      res = 1000;

    case 'M':
      res = 1000000;

    case 'B':
      res = 1000000000;

    default:
      res = 1;
  }
  return res;
}

const getEChartOpt = (data: IStudioOverallFinanceForecastCardResultItem, yAxisLeftFormatter: Function, yAxisRightFormatter: Function) => {
  const xAxisLabelList: string[] = []; // x轴刻度名列表
  const fir_item1: number[] = [], fir_item2: number[] = [], fir_item3: number[] = []; // 第一根柱子, 第二根柱子, 第二条线的数据

  if (Array.isArray(data) && data?.length) {
    for (let i = 0; i < data?.length; i++) {
      const t: any = data?.[i];
      xAxisLabelList.push(t?.date);
      fir_item1.push(t?.[BAR_LINE_OPTS?.['res_metric']?.[0]]);
      fir_item2.push(t?.[BAR_LINE_OPTS?.['res_metric']?.[1]]);
      fir_item3.push(t?.[BAR_LINE_OPTS?.['res_metric']?.[2]]);
    }
  }

  const leftPositiveList1 = fir_item1?.filter((t) => (t >= 0));
  const leftNegativeList1 = fir_item1?.filter((t) => (t < 0));
  const leftPositiveList2 = fir_item2?.filter((t) => (t >= 0));
  const leftNegativeList2 = fir_item2?.filter((t) => (t < 0));
  const leftPositiveList3 = fir_item3?.filter((t) => (t >= 0));
  const leftNegativeList3 = fir_item3?.filter((t) => (t < 0));
  const isHasNegative = leftNegativeList1?.length || leftNegativeList2?.length || leftNegativeList3?.length;
  const isAllNegative = isHasNegative && !leftPositiveList1?.length && !leftPositiveList2?.length && !leftPositiveList3?.length;
  let leftPositiveMax = Math.ceil(Math.max(...leftPositiveList1, ...leftPositiveList2, 0));
  let leftNegativeMax = Math.floor(Math.min(...leftNegativeList1, ...leftNegativeList2, 0));
  let rightPositiveMax = Math.ceil(Math.max(...leftPositiveList3, 0) * 100);
  let rightNegativeMax = Math.floor(Math.min(...leftNegativeList3, 0) * 100);
  const maxSplitNum = 5;
  /**
   * 左右轴计算数据，确保分割段数一致，刻度线对齐
   */
  // 左右轴正数及负数可以完整分割的所有段数
  const leftPositiveYSplitNumList: number[] = [],
    leftNegativeYSplitNumList: number[] = [],
    rightPositiveYSplitNumList: number[] = [],
    rightNegativeYSplitNumList: number[] = [];

  // 找出右侧正数部分数值轴可分割的段数，2段及以上开始找，最多分成5段
  for (let i = 2; i <= rightPositiveMax; i++) {
    if (i > maxSplitNum) break;
    if ((rightPositiveMax % i) === 0) {
      rightPositiveYSplitNumList.push(i);
    } else {
      continue;
    }
  }
  // 找出右侧负数部分数值轴可分割的段数，2段及以上开始找，最多分成5段
  for (let i = 2; i <= Math.abs(rightNegativeMax); i++) {
    if (i > maxSplitNum) break;
    if ((rightNegativeMax % i) === 0) {
      rightNegativeYSplitNumList.push(i);
    } else {
      continue;
    }
  }
  const symbol1 = getFormatNumPlus(leftPositiveMax)?.slice(-1);
  const num1 = getNum(symbol1);
  for (let i = 2; i <= leftPositiveMax; i++) {
    if (i > maxSplitNum) break;
    if ((leftPositiveMax % (i * num1)) === 0) {
      leftPositiveYSplitNumList.push(i);
    } else {
      continue;
    }
  }
  const symbol2 = getFormatNumPlus(Math.abs(leftNegativeMax))?.slice(-1);
  const num2 = getNum(symbol2);
  for (let i = 2; i <= Math.abs(leftNegativeMax); i++) {
    if (i > maxSplitNum) break;
    if ((leftNegativeMax % (i * num2)) === 0) {
      leftNegativeYSplitNumList.push(i);
    } else {
      continue;
    }
  }
  rightPositiveYSplitNumList?.reverse();
  let rightPositiveSplitNum = rightPositiveYSplitNumList?.[0];
  rightNegativeYSplitNumList?.reverse();
  let rightNegativeSplitNum = rightNegativeYSplitNumList?.[0];
  leftPositiveYSplitNumList?.reverse();
  let leftPositiveSplitNum = leftPositiveYSplitNumList?.[0];
  leftNegativeYSplitNumList?.reverse();
  let leftNegativeSplitNum = leftNegativeYSplitNumList?.[0];

  rightPositiveSplitNum = rightPositiveSplitNum || leftPositiveSplitNum || maxSplitNum;
  rightNegativeSplitNum = rightNegativeSplitNum || leftNegativeSplitNum || maxSplitNum;
  leftPositiveSplitNum = leftPositiveSplitNum || rightPositiveSplitNum || maxSplitNum;
  leftNegativeSplitNum = leftNegativeSplitNum || rightNegativeSplitNum || maxSplitNum;

  /**
   * 右侧部分计算
   */
  const rightPositiveSplitInterval = Math.ceil(rightPositiveMax / rightPositiveSplitNum);
  if (rightPositiveSplitNum === maxSplitNum) {
    rightPositiveMax = rightPositiveSplitInterval * maxSplitNum;
  }
  const rightNegativeSplitInterval = Math.ceil(Math.abs(rightNegativeMax) / rightNegativeSplitNum);
  if (rightNegativeSplitNum === maxSplitNum) {
    rightNegativeMax = rightNegativeSplitInterval * maxSplitNum;
  }
  const rightRealSplitInterval = Math.max(rightPositiveSplitInterval, rightNegativeSplitInterval, 0);
  if (rightRealSplitInterval > rightPositiveSplitInterval) {
    rightPositiveMax = rightPositiveSplitNum * rightRealSplitInterval;
  }
  if (rightRealSplitInterval > rightNegativeSplitInterval) {
    rightNegativeMax = rightNegativeSplitNum * rightRealSplitInterval;
  }

  /**
   * 左侧部分计算
   */
  leftPositiveMax = getSuitMaxNumber(leftPositiveMax, leftPositiveSplitNum);
  const leftPositiveSplitInterval = Math.ceil(leftPositiveMax / leftPositiveSplitNum);
  leftNegativeMax = getSuitMaxNumber(Math.abs(leftNegativeMax), leftNegativeSplitNum);
  const leftNegativeSplitInterval = Math.ceil(leftNegativeMax / leftNegativeSplitNum);
  const leftRealSplitInterval = Math.max(leftPositiveSplitInterval, leftNegativeSplitInterval, 0);
  if (leftRealSplitInterval > leftPositiveSplitInterval) {
    leftPositiveMax = leftPositiveSplitNum * leftRealSplitInterval;
  }
  if (leftRealSplitInterval > leftNegativeSplitInterval) {
    leftNegativeMax = leftNegativeSplitNum * leftRealSplitInterval;
  }

  /**
   * 统一好正数及负数部分分割的段数
   */
  if (leftPositiveSplitNum > rightPositiveSplitNum) {
    rightPositiveSplitNum = leftPositiveSplitNum;
    rightPositiveMax = leftPositiveSplitNum * rightRealSplitInterval;
  } else if (leftPositiveSplitNum < rightPositiveSplitNum) {
    leftPositiveSplitNum = rightPositiveSplitNum;
    leftPositiveMax = rightPositiveSplitNum * leftRealSplitInterval;
  }
  if (leftNegativeSplitNum > rightNegativeSplitNum) {
    rightNegativeSplitNum = leftNegativeSplitNum;
    rightNegativeMax = leftNegativeSplitNum * rightRealSplitInterval;
  } else if (leftNegativeSplitNum < rightNegativeSplitNum) {
    leftNegativeSplitNum = rightNegativeSplitNum;
    leftNegativeMax = rightNegativeSplitNum * leftRealSplitInterval;
  }
  const splitNum = isHasNegative ? leftPositiveSplitNum + leftNegativeSplitNum : leftPositiveSplitNum;

  return {
    ...BASE_ECHART_OPTION,
    grid: {
      ...BASE_ECHART_OPTION.grid,
      ...Y_AXIS_NAME_CURRENCY_GRID_OPTION,
    },
    title: {
      show: false,
    },
    legend: {
      ...BASE_ECHART_OPTION.legend,
      formatter: (name: string): string => {
        return toFirstUpperCase(name);
      },
    },
    xAxis: {
      ...BASE_ECHART_OPTION.xAxis,
      show: true,
      type: 'category',
      data: xAxisLabelList,
      axisLabel: { // 刻度文案设置
        ...BASE_ECHART_OPTION.xAxis?.axisLabel,
        margin: 16,
      },
    },
    yAxis: [
      {
        ...BASE_ECHART_OPTION.yAxis?.[0],
        ...Y_AXIS_NAME_CURRENCY_OPTION,
        type: 'value',
        scale: false,
        // name: 'left',
        axisLabel: { // 刻度文案设置
          ...BASE_ECHART_OPTION.yAxis?.[0]?.axisLabel,
          formatter: yAxisLeftFormatter,
        },
        min: isHasNegative ? (leftNegativeMax === 0 ? 0 : -Math.abs(leftNegativeMax)) : 0,
        splitNumber: splitNum,
        interval: leftRealSplitInterval,
        max: isAllNegative ? 0 : leftPositiveMax,
      },
      {
        ...BASE_ECHART_OPTION?.yAxis?.[0],
        type: 'value',
        scale: false,
        // name: 'right',
        axisLabel: { // 刻度文案设置
          ...BASE_ECHART_OPTION?.yAxis?.[0]?.axisLabel,
          formatter: yAxisRightFormatter,
        },
        min: isHasNegative ? (rightNegativeMax === 0 ? 0 : -Math.abs(rightNegativeMax) / 100) : 0,
        splitNumber: splitNum,
        interval: rightRealSplitInterval / 100,
        max: isAllNegative ? 0 : rightPositiveMax / 100,
      },
    ],
    series: [
      // 柱子
      {
        ...BASE_BAR_SERIES_OPTION,
        name: BAR_LINE_OPTS?.['metric']?.[0],
        type: 'bar',
        barMaxWidth: 64,
        barGap: '2%',
        itemStyle: {
          color: '#5086F3',
          // borderWidth: 1,
          // borderColor: '#fff',
        },
        yAxisIndex: 0,
        data: fir_item1,
        /* emphasis: {
          focus: 'series',
        }, */
        label: {
          distance: 100,
        },
      },
      // 柱子
      {
        ...BASE_BAR_SERIES_OPTION,
        name: BAR_LINE_OPTS?.['metric']?.[1],
        type: 'bar',
        barMaxWidth: 64,
        barGap: '2%',
        itemStyle: {
          color: '#77E097',
        },
        yAxisIndex: 0,
        data: fir_item2,
        /* emphasis: {
          focus: 'series',
        }, */
        label: {
          distance: 100,
        },
      },
      // 第二根线
      {
        name: BAR_LINE_OPTS?.['metric']?.[2],
        type: 'line',
        symbol: 'circle',
        symbolSize: 6, // 拐点实心点大小
        itemStyle: {
          color: ECHART_COLOR?.[7],
        },
        yAxisIndex: 1,
        data: fir_item3?.map((t, i) => {
          return {
            value: t,
            itemStyle: {
              // 前后两个点没有数据且当前点有数据时，当前数据点显示出来
              opacity: !fir_item3?.[i - 1] && t && !fir_item3?.[i + 1] ? 1 : 0,
            },
          };
        }),
        emphasis: {
          // focus: 'series',
          scale: 1.25,
          itemStyle: {
            opacity: 1,
          },
        },
      },
    ],
  };
}

// tooltip浮窗内容
const tootipFormatter = (params: any) => {
  let name = '',
    firItemListStr = '', // 第一列数据
    secItemListStr = ''; // 第二列数据

  if (Array.isArray(params) && params?.length) {
    for (let i = 0; i < params?.length; i++) {
      const t = params?.[i];
      const tVal = t?.seriesName === 'Profit Margin' ? getFormatPercentPlus(t?.value, 2) : getFormatNumPlus(t?.value);
      if (!name) name = t?.name;
      firItemListStr += `
        <div style="display: flex; flex-wrap: nowrap; align-items: center; margin-top: 6px;">
          <span class="general-circular-dot" style="background-color: ${t?.color}; margin-right: 8px;"></span>
          <div class="general-item body-text" style="margin: 0px; max-width: 124px;">${t?.seriesName}</div>
        </div>
      `;
      secItemListStr += `
        <div class="general-item body-num" style="margin-top: 6px; line-height: 20px; font-weight: 700; font-size: 14px; text-align: right;">${tVal}</div>
      `;
    }
  }

  const htmlStr = `
    <section class="echart-tooltip-style-content" style="font-weight: 400; font-size: 12px; color: #ebecf1;">
      <div class="general-item" style="display: flex; justify-content: space-between; margin: 0px; width: 100%; height: auto;">
        <div>${name || '-'}</div>
      </div>
      <div class="general-item" style="display: flex; justify-content: space-between; margin: 0px; width: 100%; height: auto;">
        <div>
          ${firItemListStr}
        </div>
        <div style="margin-left: 30px; text-align: right">
          ${secItemListStr}
        </div>
      </div>
    <section>
  `;

  return htmlStr;
}

export {
  getEChartOpt,
  tootipFormatter,
}
